import type { Content } from '@tiptap/core';
import type { Editor } from '@tiptap/react';

import { riskAssessmentTemplate, soapTemplate } from './editorTemplates';

export type SlashGroup = 'Headings' | 'Lists' | 'Inserts' | 'Clinical scaffolds';

export interface SlashItem {
  id: string;
  title: string;
  description: string;
  group: SlashGroup;
  keywords: string[];
  command: (editor: Editor, range: { from: number; to: number }) => void;
}

const insertContent = (
  editor: Editor,
  range: { from: number; to: number },
  content: Content,
): void => {
  editor.chain().focus().deleteRange(range).insertContent(content).run();
};

export function buildSlashItems(kind: 'note' | 'case-report'): SlashItem[] {
  const headings: SlashItem[] = [
    {
      id: 'heading-1',
      title: 'Heading 1',
      description: 'Top-level heading',
      group: 'Headings',
      keywords: ['heading', 'h1', 'title'],
      command: (editor, range) => {
        editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run();
      },
    },
    {
      id: 'heading-2',
      title: 'Heading 2',
      description: 'Section heading',
      group: 'Headings',
      keywords: ['heading', 'h2', 'section'],
      command: (editor, range) => {
        editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run();
      },
    },
    {
      id: 'heading-3',
      title: 'Heading 3',
      description: 'Sub-section heading',
      group: 'Headings',
      keywords: ['heading', 'h3'],
      command: (editor, range) => {
        editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run();
      },
    },
  ];

  const lists: SlashItem[] = [
    {
      id: 'bullet-list',
      title: 'Bullet list',
      description: 'Unordered list',
      group: 'Lists',
      keywords: ['list', 'bullet', 'ul'],
      command: (editor, range) => {
        editor.chain().focus().deleteRange(range).toggleBulletList().run();
      },
    },
    {
      id: 'ordered-list',
      title: 'Numbered list',
      description: 'Ordered list',
      group: 'Lists',
      keywords: ['list', 'numbered', 'ol'],
      command: (editor, range) => {
        editor.chain().focus().deleteRange(range).toggleOrderedList().run();
      },
    },
    {
      id: 'task-list',
      title: 'Task list',
      description: 'Checkbox list',
      group: 'Lists',
      keywords: ['list', 'task', 'todo', 'checkbox'],
      command: (editor, range) => {
        editor.chain().focus().deleteRange(range).toggleTaskList().run();
      },
    },
  ];

  const inserts: SlashItem[] = [
    {
      id: 'quote',
      title: 'Quote',
      description: 'Cite the client verbatim',
      group: 'Inserts',
      keywords: ['quote', 'blockquote'],
      command: (editor, range) => {
        editor.chain().focus().deleteRange(range).toggleBlockquote().run();
      },
    },
    {
      id: 'divider',
      title: 'Section break',
      description: 'A printed-page rule',
      group: 'Inserts',
      keywords: ['divider', 'separator', 'hr', 'rule', 'break'],
      command: (editor, range) => {
        editor.chain().focus().deleteRange(range).setHorizontalRule().run();
      },
    },
    {
      id: 'code-block',
      title: 'Code block',
      description: 'For codes, scores, references',
      group: 'Inserts',
      keywords: ['code', 'codeblock', 'pre'],
      command: (editor, range) => {
        editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
      },
    },
  ];

  const templates: SlashItem[] =
    kind === 'note'
      ? [
          {
            id: 'template-soap',
            title: 'SOAP scaffold',
            description: 'Subjective · Objective · Assessment · Plan',
            group: 'Clinical scaffolds',
            keywords: ['template', 'soap', 'clinical'],
            command: (editor, range) => {
              insertContent(editor, range, soapTemplate);
            },
          },
          {
            id: 'template-risk',
            title: 'Risk-assessment scaffold',
            description: 'Factors · Protective · Level · Safety · Follow-up',
            group: 'Clinical scaffolds',
            keywords: ['template', 'risk', 'assessment', 'clinical'],
            command: (editor, range) => {
              insertContent(editor, range, riskAssessmentTemplate);
            },
          },
        ]
      : [];

  return [...headings, ...lists, ...inserts, ...templates];
}

export function filterItems(items: SlashItem[], query: string): SlashItem[] {
  const trimmed = query.trim().toLowerCase();
  if (trimmed === '') return items;
  return items.filter((item) => {
    const haystack = [item.title, ...item.keywords].join(' ').toLowerCase();
    return haystack.includes(trimmed);
  });
}
